import React, { useState, useEffect } from 'react';
import { useConfirm } from '../common/ConfirmProvider';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiPlus, FiCalendar, FiUsers, FiTrendingUp, FiTrendingDown, FiEdit3, FiTrash2, FiGrid, FiSettings, FiRefreshCw, FiEye, FiMoreVertical } from 'react-icons/fi';
import { AnimatePresence } from 'framer-motion';
import { formatDateForDisplay } from '../../utils/datetime-comprehensive';
import apiService from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import CreateAttendanceModal from './CreateAttendanceModal';
import TakeAttendanceModal from './TakeAttendanceModal';
import CourseSelectionModal from './CourseSelectionModal';
import SessionSelectionModal from './SessionSelectionModal';
import QRCodeModal from './QRCodeModal';
import EditAttendanceModal from './EditAttendanceModal';
import Notification from '../common/Notification';
import logger from '../../utils/logger';

const ATTENDANCE_PERMISSION_KEY = 'course_attendance:manage';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1.5rem;
  
  @media (max-width: 768px) {
    padding: 1.5rem 1rem;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const HeaderLeft = styled.div`
  flex: 1;
`;

const Title = styled.h1`
  font-family: 'Inter', sans-serif;
  font-size: 1.875rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 0.25rem 0;
  letter-spacing: -0.025em;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const Subtitle = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  color: var(--text-secondary);
  margin: 0;
  
  @media (max-width: 768px) {
    font-size: 0.8125rem;
  }
`;

const CreateButton = styled.button`
  background: var(--primary-color);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 10px;
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  white-space: nowrap;
  
  &:hover {
    background: var(--primary-hover, #c9a066);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled(motion.div)`
  background: white;
  border: 1px solid #E5E3DF;
  border-radius: 16px;
  padding: 1.25rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
  
  &:hover {
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  }
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
`;

const StatTitle = styled.h3`
  font-family: 'Inter', sans-serif;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-secondary);
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const StatIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.color ? `${props.color}15` : 'rgba(216, 184, 137, 0.1)'};
  color: ${props => props.color || 'var(--primary-color)'};
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const StatValue = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 0.25rem;
  line-height: 1.2;
`;

const StatChange = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 0.75rem;
  color: ${props => props.$positive ? '#10b981' : '#ef4444'};
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const SessionsSection = styled.div`
  margin-top: 2rem;
`;

const SessionsSectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
`;

const SessionsSectionTitle = styled.h2`
  font-family: 'Inter', sans-serif;
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
`;

const SessionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ScheduledCard = styled.div`
  background: var(--background-primary);
  border: 1px solid var(--border-color);
  border-left: 4px solid #B89569;
  border-radius: 10px;
  padding: 1rem 1.25rem;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 1rem;
  align-items: center;
`;

const ScheduledMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
`;

const ScheduledTitle = styled.div`
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const ScheduledStatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  border-radius: 4px;
  background: ${(p) =>
    p.$status === 'pending' ? 'rgba(184, 149, 105, 0.15)' :
    p.$status === 'created' ? 'rgba(22, 163, 74, 0.12)' :
    p.$status === 'failed' ? 'rgba(220, 38, 38, 0.12)' :
    'rgba(107, 114, 128, 0.12)'};
  color: ${(p) =>
    p.$status === 'pending' ? '#9A7A4F' :
    p.$status === 'created' ? '#15803d' :
    p.$status === 'failed' ? '#b91c1c' :
    '#6b7280'};
`;

const ScheduledDetailRow = styled.div`
  font-size: 0.8125rem;
  color: var(--text-secondary);
`;

const ScheduledError = styled.div`
  font-size: 0.75rem;
  color: #b91c1c;
  margin-top: 0.25rem;
`;

const ScheduledCancelButton = styled.button`
  background: transparent;
  border: 1px solid #b91c1c;
  color: #b91c1c;
  font-weight: 600;
  font-size: 0.8125rem;
  padding: 6px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-family: 'Inter', sans-serif;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: #b91c1c;
    color: white;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ScheduledRetryButton = styled.button`
  background: transparent;
  border: 1px solid #047857;
  color: #047857;
  font-weight: 600;
  font-size: 0.8125rem;
  padding: 6px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-family: 'Inter', sans-serif;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: #047857;
    color: white;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ScheduledList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const SessionCard = styled(motion.div)`
  background: white;
  border: 1px solid #E5E3DF;
  border-radius: 16px;
  padding: 1.25rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  
  &:hover {
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const SessionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
`;

const SessionInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const SessionTitle = styled.h3`
  font-family: 'Inter', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.375rem 0;
  line-height: 1.4;
`;

const SessionDate = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 0.8125rem;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  gap: 0.375rem;
`;

const SessionStats = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-top: 0.5rem;
`;

const StatBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  background: #f9f9f9;
  border-radius: 8px;
  font-family: 'Inter', sans-serif;
  font-size: 0.75rem;
  color: var(--text-secondary);
  
  svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }
`;

const AttendanceMetrics = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
  padding: 1rem;
  background: #f9f9f9;
  border-radius: 12px;
`;

const MetricItem = styled.div`
  text-align: center;
`;

const MetricValue = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 1.5rem;
  font-weight: 700;
  color: ${props => {
    if (props.type === 'present') return '#10b981';
    if (props.type === 'absent') return '#ef4444';
    return 'var(--text-primary)';
  }};
  margin-bottom: 0.25rem;
  line-height: 1.2;
`;

const MetricLabel = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const AttendanceRatePill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 0.875rem;
  background: rgba(216, 184, 137, 0.1);
  color: var(--primary-color);
  border-radius: 999px;
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  margin-top: 0.5rem;
`;

const SessionActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 0.5rem;
`;

const ManageDropdown = styled.div`
  position: relative;
  display: inline-block;
`;

const ManageButton = styled.button`
  background: white;
  color: var(--text-primary);
  border: 1px solid #E5E3DF;
  padding: 0.625rem 1rem;
  border-radius: 10px;
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    background: #f9f9f9;
    border-color: var(--primary-color);
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const DropdownMenu = styled(motion.div)`
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  background: white;
  border: 1px solid #E5E3DF;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 100;
  min-width: 180px;
  overflow: hidden;
`;

const DropdownItem = styled.button`
  width: 100%;
  padding: 0.75rem 1rem;
  background: none;
  border: none;
  text-align: left;
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  &:hover {
    background: #f9f9f9;
  }
  
  &.danger {
    color: #ef4444;
    
    &:hover {
      background: rgba(239, 68, 68, 0.1);
    }
  }
  
  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
`;

const DropdownBackdrop = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 99;
  background: transparent;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  color: var(--text-secondary);
  
  @media (max-width: 768px) {
    padding: 2rem 1rem;
  }
`;

const LoadingSpinner = styled.div`
  width: 24px;
  height: 24px;
  border: 2px solid transparent;
  border-top: 2px solid var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const CourseSelector = styled.div`
  background: white;
  border: 1px solid #E5E3DF;
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const CourseSelectorTitle = styled.h2`
  font-family: 'Inter', sans-serif;
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 0.5rem 0;
  text-align: left;
  
  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const CourseSelectorDescription = styled.p`
  font-family: 'Inter', sans-serif;
  color: var(--text-secondary);
  margin: 0 0 2rem 0;
  font-size: 0.875rem;
  text-align: left;
  line-height: 1.5;
`;

const SectionTitle = styled.h3`
  font-family: 'Inter', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 2rem 0 1rem 0;
  text-align: left;
  
  &:first-of-type {
    margin-top: 0;
  }
  
  @media (max-width: 768px) {
    font-size: 0.9375rem;
    margin-top: 1.5rem;
  }
`;

const CourseGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const CourseCard = styled(motion.div)`
  background: white;
  border: 1px solid #E5E3DF;
  border-radius: 16px;
  padding: 1.25rem;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  
  &:hover {
    border-color: var(--primary-color);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const CourseHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const CourseCardIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: var(--primary-color);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Inter', sans-serif;
  font-size: 1.25rem;
  font-weight: 700;
  flex-shrink: 0;
`;

const CourseCardInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const CourseCardName = styled.h3`
  font-family: 'Inter', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.375rem 0;
  line-height: 1.4;
`;

const CourseCardCode = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 0.8125rem;
  color: var(--text-secondary);
  margin: 0;
`;

const CourseStats = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 0.75rem;
  border-top: 1px solid #f0f0f0;
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--primary-color);
  
  span:last-child {
    color: var(--text-secondary);
    font-size: 1rem;
  }
`;

const CourseBar = styled.div`
  background: white;
  border: 1px solid #E5E3DF;
  border-radius: 16px;
  padding: 1.25rem 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const CourseBarContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const CourseInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
`;

const CourseIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: var(--primary-color);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Inter', sans-serif;
  font-size: 1.25rem;
  font-weight: 700;
  flex-shrink: 0;
`;

const CourseDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

const CourseName = styled.h3`
  font-family: 'Inter', sans-serif;
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.25rem 0;
  line-height: 1.3;
`;

const CourseCodePill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  background: rgba(216, 184, 137, 0.1);
  color: var(--primary-color);
  border-radius: 999px;
  font-family: 'Inter', sans-serif;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.025em;
`;

const CourseActions = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    width: 100%;
    flex-direction: column;
  }
  
  @media (min-width: 769px) and (max-width: 1024px) {
    flex-direction: column;
  }
`;

const PrimaryButton = styled.button`
  background: var(--primary-color);
  color: white;
  border: none;
  padding: 0.625rem 1.25rem;
  border-radius: 10px;
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  white-space: nowrap;
  
  &:hover {
    background: var(--primary-hover, #c9a066);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const SecondaryButton = styled.button`
  background: white;
  color: var(--text-primary);
  border: 1px solid #E5E3DF;
  padding: 0.625rem 1.25rem;
  border-radius: 10px;
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;
  
  &:hover {
    background: #f9f9f9;
    border-color: var(--primary-color);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;





const EmptyIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 1rem;
  opacity: 0.5;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 0.75rem;
  }
`;

const EmptyTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  color: var(--text-primary);
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const EmptyDescription = styled.p`
  font-size: 0.875rem;
  margin: 0;
  opacity: 0.8;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

function AttendanceManagement() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedElective, setSelectedElective] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTakeAttendanceModal, setShowTakeAttendanceModal] = useState(false);
  const [liveSessionStats, setLiveSessionStats] = useState(null);
  const [showCourseSelection, setShowCourseSelection] = useState(false);
  const [showQRCodeModal, setShowQRCodeModal] = useState(false);
  const [selectedQRCodeSession, setSelectedQRCodeSession] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedEditSession, setSelectedEditSession] = useState(null);
  const [courses, setCourses] = useState([]);
  const [electives, setElectives] = useState([]);
  const [notification, setNotification] = useState({ isOpen: false, type: 'success', title: '', message: '' });
  const [showSessionSelection, setShowSessionSelection] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [scheduledSessions, setScheduledSessions] = useState([]);
  const [cancellingScheduleId, setCancellingScheduleId] = useState(null);
  const [retryingScheduleId, setRetryingScheduleId] = useState(null);

  const selectedEntity = selectedCourse || selectedElective;
  const isElective = !!selectedElective;

  const isAdmin = user?.role === 'admin';

  const fetchScopesAndCourses = React.useCallback(async () => {
    try {
      const [grantedRes, coursesRes, electivesRes] = await Promise.allSettled([
        apiService.request('/permissions/my-granted-scopes'),
        apiService.getCourses(),
        apiService.getElectives(),
      ]);

      // Build sets of granted attendance scopes (only those with attendance permission)
      const grantedCourseIds = new Set();
      const grantedElectiveIds = new Set();
      if (grantedRes.status === 'fulfilled' && grantedRes.value?.success) {
        grantedRes.value.scopes.forEach(s => {
          if (s.permissions.includes(ATTENDANCE_PERMISSION_KEY)) {
            if (s.scope_type === 'course') grantedCourseIds.add(s.scope_id);
            if (s.scope_type === 'elective') grantedElectiveIds.add(s.scope_id);
          }
        });
      }

      // Build allowed courses: instructor's own courses + granted courses
      const allCourses = coursesRes.status === 'fulfilled' && coursesRes.value?.success
        ? (coursesRes.value.courses || [])
        : [];
      const allowedCourses = allCourses.filter(c => {
        if (user?.role === 'instructor' && c.instructor_id === user.id) return true;
        return grantedCourseIds.has(c.id);
      });

      // Add granted courses that aren't in the returned list (e.g., student who isn't enrolled)
      const fetchedCourseIds = new Set(allCourses.map(c => c.id));
      const missingGrantedCourses = [];
      for (const id of grantedCourseIds) {
        if (!fetchedCourseIds.has(id)) {
          try {
            const fallback = await apiService.request(`/courses/${id}`);
            if (fallback.success && fallback.course) {
              missingGrantedCourses.push(fallback.course);
            }
          } catch (e) {
            // Skip courses we can't fetch
          }
        }
      }
      setCourses([...allowedCourses, ...missingGrantedCourses]);

      // Same for electives
      const allElectives = electivesRes.status === 'fulfilled' && electivesRes.value?.success
        ? (electivesRes.value.electives || [])
        : [];
      const allowedElectives = allElectives.filter(e => {
        if (user?.role === 'instructor' && (e.instructor_id === user.id)) return true;
        return grantedElectiveIds.has(e.id);
      });
      setElectives(allowedElectives);
    } catch (error) {
      logger.error('Error fetching scopes and courses', { error });
    }
  }, [user]);

  useEffect(() => {
    if (isAdmin) {
      fetchCourses();
      fetchElectives();
    } else {
      fetchScopesAndCourses();
    }
  }, [isAdmin, fetchScopesAndCourses]);

  useEffect(() => {
    if (selectedCourse) {
      fetchSessionsForCourse(selectedCourse.id);
    } else if (selectedElective) {
      fetchSessionsForElective(selectedElective.id);
    }
  }, [selectedCourse, selectedElective]);

  // Fetch pending/created/failed/cancelled scheduled sessions for the selected
  // course or elective. Refreshes whenever the entity changes or after a
  // session is created/scheduled (handleSessionCreated triggers a refetch).
  const fetchScheduledSessions = React.useCallback(async () => {
    if (!selectedCourse && !selectedElective) {
      setScheduledSessions([]);
      return;
    }
    try {
      const res = await apiService.getScheduledAttendanceSessions({
        courseId: selectedCourse?.id,
        electiveId: selectedElective?.id,
      });
      if (res?.success) {
        setScheduledSessions(Array.isArray(res.schedules) ? res.schedules : []);
      } else {
        setScheduledSessions([]);
      }
    } catch (error) {
      console.error('Failed to fetch scheduled attendance sessions', error);
      setScheduledSessions([]);
    }
  }, [selectedCourse, selectedElective]);

  useEffect(() => {
    fetchScheduledSessions();
  }, [fetchScheduledSessions]);

  const handleCancelScheduledSession = async (id) => {
    if (cancellingScheduleId) return;
    setCancellingScheduleId(id);
    try {
      const res = await apiService.cancelScheduledAttendanceSession(id);
      if (res?.success) {
        setNotification({
          isOpen: true,
          type: 'success',
          title: 'Cancelled',
          message: 'Scheduled session cancelled.',
        });
        await fetchScheduledSessions();
      } else {
        setNotification({
          isOpen: true,
          type: 'error',
          title: 'Error',
          message: res?.message || 'Failed to cancel scheduled session',
        });
      }
    } catch (error) {
      setNotification({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: 'Failed to cancel scheduled session',
      });
    } finally {
      setCancellingScheduleId(null);
    }
  };

  const handleRetryScheduledSession = async (id) => {
    if (retryingScheduleId) return;
    setRetryingScheduleId(id);
    try {
      const res = await apiService.retryScheduledAttendanceSession(id);
      if (res?.success) {
        setNotification({
          isOpen: true,
          type: 'success',
          title: 'Session Created',
          message: 'The attendance session was created successfully.',
        });
        await fetchScheduledSessions();
      } else {
        setNotification({
          isOpen: true,
          type: 'error',
          title: 'Retry Failed',
          message: res?.message || 'Failed to retry scheduled session',
        });
        await fetchScheduledSessions();
      }
    } catch (error) {
      setNotification({
        isOpen: true,
        type: 'error',
        title: 'Retry Failed',
        message: error?.message || 'Failed to retry scheduled session',
      });
    } finally {
      setRetryingScheduleId(null);
    }
  };

  // Auto-select course/elective from URL query params (e.g., ?courseId=5)
  useEffect(() => {
    const courseIdParam = searchParams.get('courseId');
    const electiveIdParam = searchParams.get('electiveId');
    if (!courseIdParam && !electiveIdParam) return;
    if (selectedCourse || selectedElective) return;

    if (courseIdParam && courses.length > 0) {
      const match = courses.find(c => String(c.id) === courseIdParam);
      if (match) {
        setSelectedCourse(match);
        searchParams.delete('courseId');
        setSearchParams(searchParams, { replace: true });
      }
    } else if (electiveIdParam && electives.length > 0) {
      const match = electives.find(e => String(e.id) === electiveIdParam);
      if (match) {
        setSelectedElective(match);
        searchParams.delete('electiveId');
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [searchParams, courses, electives, selectedCourse, selectedElective, setSearchParams]);

  const fetchCourses = async () => {
    try {
      const response = await apiService.getCourses();
      if (response.success) {
        setCourses(response.courses);
      }
    } catch (error) {
      logger.error('Error fetching courses', { error });
    }
  };

  const fetchElectives = async () => {
    try {
      const response = await apiService.getElectives();
      if (response.success) {
        setElectives(response.electives);
      }
    } catch (error) {
      logger.error('Error fetching electives', { error });
    }
  };

  const fetchSessionsForCourse = async (courseId) => {
    try {
      setLoading(true);
      
      const sessionsResponse = await apiService.getAttendanceSessions(courseId);
      
      if (sessionsResponse.success) {
        setSessions(sessionsResponse.sessions);
      }
    } catch (error) {
      logger.error('Error fetching attendance sessions', { error, courseId });
      setNotification({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: 'Failed to fetch attendance sessions'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchSessionsForElective = async (electiveId) => {
    try {
      setLoading(true);
      
      const sessionsResponse = await apiService.getElectiveAttendanceSessions(electiveId);
      
      if (sessionsResponse.success) {
        setSessions(sessionsResponse.sessions);
      }
    } catch (error) {
      logger.error('Error fetching elective attendance sessions', { error, electiveId });
      setNotification({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: 'Failed to fetch elective attendance sessions'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSession = () => {
    setShowCourseSelection(true);
  };

  const handleCourseSelected = (course) => {
    setSelectedCourse(course);
    setSelectedElective(null);
    setShowCourseSelection(false);
    setShowCreateModal(true);
  };

  const handleElectiveSelected = (elective) => {
    setSelectedElective(elective);
    setSelectedCourse(null);
    setShowCourseSelection(false);
    setShowCreateModal(true);
  };

  const handleSelectCourseForViewing = (course) => {
    setSelectedCourse(course);
    setSelectedElective(null);
  };

  const handleSelectElectiveForViewing = (elective) => {
    setSelectedElective(elective);
    setSelectedCourse(null);
  };

  const handleChangeCourse = () => {
    setSelectedCourse(null);
    setSelectedElective(null);
    setSessions([]);
  };


  const handleViewStudents = () => {
    if (selectedCourse) {
      navigate(`/admin/courses/${selectedCourse.id}/students`);
    } else if (selectedElective) {
      navigate(`/admin/electives/${selectedElective.id}/students`);
    }
  };

  const handleViewAttendance = () => {
    if (!selectedEntity || sessions.length === 0) return;
    setShowSessionSelection(true);
  };

  const handleSessionSelected = (session) => {
    setSelectedSession(session);
    setShowSessionSelection(false);
    setShowTakeAttendanceModal(true);
  };

  const handleShowQRCode = (session) => {
    setSelectedQRCodeSession(session);
    setShowQRCodeModal(true);
  };

  const handleEditSession = (session) => {
    setSelectedEditSession(session);
    setShowEditModal(true);
  };

  const handleSessionUpdated = () => {
    if (selectedCourse) {
      fetchSessionsForCourse(selectedCourse.id);
    } else if (selectedElective) {
      fetchSessionsForElective(selectedElective.id);
    }
    setNotification({
      isOpen: true,
      type: 'success',
      title: 'Success',
      message: 'Attendance session updated successfully'
    });
  };

  const handleSessionCreated = async (session) => {
    // Refetch sessions to get complete data with statistics
    if (selectedCourse) {
      await fetchSessionsForCourse(selectedCourse.id);
    } else if (selectedElective) {
      await fetchSessionsForElective(selectedElective.id);
    }
    if (session?.scheduled) {
      await fetchScheduledSessions();
    }
    setShowCreateModal(false);
    setNotification({
      isOpen: true,
      type: 'success',
      title: 'Success',
      message: session?.scheduled
        ? 'Attendance session scheduled successfully — it will be created automatically at the set time.'
        : 'Attendance session created successfully'
    });
  };

  const handleTakeAttendance = (session) => {
    setSelectedSession(session);
    setShowTakeAttendanceModal(true);
  };

  const handleAttendanceUpdated = async () => {
    // Add a small delay to ensure database transaction is committed
    await new Promise(resolve => setTimeout(resolve, 500));
    if (selectedCourse) {
      await fetchSessionsForCourse(selectedCourse.id);
    } else if (selectedElective) {
      await fetchSessionsForElective(selectedElective.id);
    }
    setShowTakeAttendanceModal(false);
    setLiveSessionStats(null); // Clear live stats when modal closes
    setNotification({
      isOpen: true,
      type: 'success',
      title: 'Success',
      message: 'Attendance updated successfully'
    });
  };

  const handleAttendanceChanged = (newStats) => {
    setLiveSessionStats(newStats);
  };

  const handleDeleteSession = async (sessionId) => {
    if (!(await confirm({ title: 'Delete attendance session?', message: 'This action cannot be undone.', confirmText: 'Delete', danger: true }))) {
      return;
    }

    try {
      const response = await apiService.deleteAttendanceSession(sessionId);
      if (response.success) {
        setSessions(prev => prev.filter(session => session.id !== sessionId));
        setNotification({
          isOpen: true,
          type: 'success',
          title: 'Success',
          message: 'Attendance session deleted successfully'
        });
      } else {
        setNotification({
          isOpen: true,
          type: 'error',
          title: 'Error',
          message: response.message || 'Failed to delete attendance session'
        });
      }
    } catch (error) {
      logger.error('Error deleting attendance session', { error, sessionId });
      setNotification({
        isOpen: true,
        type: 'error',
        title: 'Error',
        message: 'Failed to delete attendance session'
      });
    }
  };

  const calculateStats = () => {
    const totalSessions = sessions.length;
    // Get unique enrolled students count from the first session (all sessions should have the same enrolled students)
    // This avoids counting students multiple times if they appear in multiple sessions
    const totalStudents = sessions.length > 0 ? (sessions[0].total_students || 0) : 0;
    const totalPresent = sessions.reduce((sum, session) => sum + (session.present_count || 0), 0);
    const averageRate = totalSessions > 0 
      ? Math.round(sessions.reduce((sum, session) => sum + (session.attendance_rate || 0), 0) / totalSessions)
      : 0;

    return {
      totalSessions,
      totalStudents,
      totalPresent,
      averageRate
    };
  };

  const stats = calculateStats();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdownId && !event.target.closest('[data-dropdown]')) {
        setOpenDropdownId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdownId]);

  if (loading) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: '3rem', fontFamily: "'Inter', sans-serif" }}>
          <LoadingSpinner />
          <p style={{ marginTop: '1rem', color: 'var(--text-secondary)' }}>Loading attendance sessions...</p>
        </div>
      </Container>
    );
  }

  // Show course/elective selection if no entity is selected
  if (!selectedEntity) {
    return (
      <Container>
        <Header>
          <HeaderLeft>
            <Title>Attendance Management</Title>
            <Subtitle>Track sessions and attendance.</Subtitle>
          </HeaderLeft>
        </Header>

        <CourseSelector>
          <CourseSelectorTitle>Select a Course or Elective</CourseSelectorTitle>
          <CourseSelectorDescription>
            Choose a course or elective to view and manage its attendance sessions.
          </CourseSelectorDescription>

          {courses.length > 0 && (
            <>
              <SectionTitle>Courses</SectionTitle>
              <CourseGrid>
                {courses.map((course) => (
                  <CourseCard
                    key={course.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    whileHover={{ scale: 1.01 }}
                    onClick={() => handleSelectCourseForViewing(course)}
                  >
                    <CourseHeader>
                      <CourseCardIcon>
                        {course.code.charAt(0)}
                      </CourseCardIcon>
                      <CourseCardInfo>
                        <CourseCardName>{course.name}</CourseCardName>
                        <CourseCardCode>{course.code}</CourseCardCode>
                      </CourseCardInfo>
                    </CourseHeader>
                    <CourseStats>
                      <span>View Sessions</span>
                      <span>→</span>
                    </CourseStats>
                  </CourseCard>
                ))}
              </CourseGrid>
            </>
          )}

          {electives.length > 0 && (
            <>
              <SectionTitle>Electives</SectionTitle>
              <CourseGrid>
                {electives.map((elective) => (
                  <CourseCard
                    key={elective.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    whileHover={{ scale: 1.01 }}
                    onClick={() => handleSelectElectiveForViewing(elective)}
                  >
                    <CourseHeader>
                      <CourseCardIcon>
                        {elective.code.charAt(0)}
                      </CourseCardIcon>
                      <CourseCardInfo>
                        <CourseCardName>{elective.name}</CourseCardName>
                        <CourseCardCode>{elective.code} (Elective)</CourseCardCode>
                      </CourseCardInfo>
                    </CourseHeader>
                    <CourseStats>
                      <span>View Sessions</span>
                      <span>→</span>
                    </CourseStats>
                  </CourseCard>
                ))}
              </CourseGrid>
            </>
          )}

          {courses.length === 0 && electives.length === 0 && (
            <EmptyState>
              <EmptyIcon>📚</EmptyIcon>
              <EmptyTitle>No Courses or Electives</EmptyTitle>
              <EmptyDescription>
                There are no courses or electives available to manage attendance for.
              </EmptyDescription>
            </EmptyState>
          )}
        </CourseSelector>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <Title>Attendance Management</Title>
          <Subtitle>Track sessions and attendance.</Subtitle>
        </HeaderLeft>
        <CreateButton onClick={handleCreateSession}>
          <FiPlus size={16} />
          Create Session
        </CreateButton>
      </Header>
      {selectedEntity && (
        <CourseBar>
          <CourseBarContent>
            <CourseInfo>
              <CourseIcon>
                {selectedEntity.code.charAt(0)}
              </CourseIcon>
              <CourseDetails>
                <CourseName>{selectedEntity.name}</CourseName>
                <CourseCodePill>{selectedEntity.code} {isElective ? '• Elective' : ''}</CourseCodePill>
              </CourseDetails>
            </CourseInfo>
            <CourseActions>
              <PrimaryButton onClick={handleViewAttendance}>
                <FiEye size={16} />
                View Attendance
              </PrimaryButton>
              <SecondaryButton onClick={handleViewStudents}>
                <FiUsers size={16} />
                View Students
              </SecondaryButton>
              <SecondaryButton onClick={handleChangeCourse}>
                <FiRefreshCw size={16} />
                Change Course
              </SecondaryButton>
            </CourseActions>
          </CourseBarContent>
        </CourseBar>
      )}
      {selectedEntity && (
        <StatsGrid>
          <StatCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <StatHeader>
              <StatTitle>Total Sessions</StatTitle>
              <StatIcon color="#3b82f6">
                <FiCalendar />
              </StatIcon>
            </StatHeader>
            <StatValue>{stats.totalSessions}</StatValue>
            <StatChange $positive={true}>
              <FiTrendingUp size={12} />
              This month
            </StatChange>
          </StatCard>

          <StatCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <StatHeader>
              <StatTitle>Total Students</StatTitle>
              <StatIcon color="#10b981">
                <FiUsers />
              </StatIcon>
            </StatHeader>
            <StatValue>{stats.totalStudents}</StatValue>
            <StatChange $positive={true}>
              <FiTrendingUp size={12} />
              Enrolled
            </StatChange>
          </StatCard>

          <StatCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <StatHeader>
              <StatTitle>Avg Attendance</StatTitle>
              <StatIcon color="#f59e0b">
                <FiTrendingUp />
              </StatIcon>
            </StatHeader>
            <StatValue>{stats.averageRate}%</StatValue>
            <StatChange $positive={stats.averageRate >= 80}>
              {stats.averageRate >= 80 ? <FiTrendingUp size={12} /> : <FiTrendingDown size={12} />}
              {stats.averageRate >= 80 ? 'Good' : 'Needs improvement'}
            </StatChange>
          </StatCard>
        </StatsGrid>
      )}
      {selectedEntity && (
        <SessionsSection>
          <SessionsSectionHeader>
            <SessionsSectionTitle>Sessions</SessionsSectionTitle>
          </SessionsSectionHeader>
          {sessions.length === 0 ? (
            <EmptyState>
              <EmptyIcon>📊</EmptyIcon>
              <EmptyTitle>No Attendance Sessions</EmptyTitle>
              <EmptyDescription>
                Create your first attendance session to start tracking student attendance.
              </EmptyDescription>
            </EmptyState>
          ) : (
            <SessionsGrid>
              {sessions.map((session, index) => {
                const isOpen = openDropdownId === session.id;
                const presentCount = showTakeAttendanceModal && selectedSession && selectedSession.id === session.id && liveSessionStats 
                  ? liveSessionStats.present_count 
                  : session.present_count || 0;
                const absentCount = showTakeAttendanceModal && selectedSession && selectedSession.id === session.id && liveSessionStats 
                  ? liveSessionStats.absent_count 
                  : session.absent_count || 0;
                const attendanceRate = showTakeAttendanceModal && selectedSession && selectedSession.id === session.id && liveSessionStats 
                  ? liveSessionStats.attendance_rate 
                  : session.attendance_rate || 0;
                const sessionDate = formatDateForDisplay(session.date, 'MMM d, yyyy');
                const sessionTitle = session.title || `Week ${index + 1}`;
                
                return (
                  <SessionCard
                    key={session.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0 }}
                  >
                    <SessionHeader>
                      <SessionInfo>
                        <SessionTitle>{sessionTitle}</SessionTitle>
                        <SessionDate>
                          <FiCalendar size={14} />
                          {sessionDate}
                        </SessionDate>
                        <SessionStats>
                          <StatBadge>
                            <FiUsers size={14} />
                            {session.total_students || 0} students
                          </StatBadge>
                        </SessionStats>
                      </SessionInfo>
                      <SessionActions>
                        <ManageDropdown data-dropdown>
                          <ManageButton
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenDropdownId(isOpen ? null : session.id);
                            }}
                          >
                            Manage
                            <FiMoreVertical />
                          </ManageButton>
                          <AnimatePresence>
                            {isOpen && (
                              <>
                                <DropdownBackdrop
                                  initial={{ opacity: 0 }}
                                  animate={{ opacity: 1 }}
                                  exit={{ opacity: 0 }}
                                  onClick={() => setOpenDropdownId(null)}
                                />
                                <DropdownMenu
                                  initial={{ opacity: 0, y: -10 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  exit={{ opacity: 0, y: -10 }}
                                  transition={{ duration: 0.2 }}
                                >
                                  <DropdownItem
                                    onClick={() => {
                                      handleTakeAttendance(session);
                                      setOpenDropdownId(null);
                                    }}
                                  >
                                    <FiEdit3 />
                                    {session.end_time ? 'View Attendance' : 'Take Attendance'}
                                  </DropdownItem>
                                  <DropdownItem
                                    onClick={() => {
                                      handleEditSession(session);
                                      setOpenDropdownId(null);
                                    }}
                                  >
                                    <FiSettings />
                                    Edit Session
                                  </DropdownItem>
                                  <DropdownItem
                                    onClick={() => {
                                      handleShowQRCode(session);
                                      setOpenDropdownId(null);
                                    }}
                                  >
                                    <FiGrid />
                                    View QR Code
                                  </DropdownItem>
                                  <DropdownItem
                                    className="danger"
                                    onClick={() => {
                                      handleDeleteSession(session.id);
                                      setOpenDropdownId(null);
                                    }}
                                  >
                                    <FiTrash2 />
                                    Delete Session
                                  </DropdownItem>
                                </DropdownMenu>
                              </>
                            )}
                          </AnimatePresence>
                        </ManageDropdown>
                      </SessionActions>
                    </SessionHeader>
                    
                    <AttendanceMetrics>
                      <MetricItem>
                        <MetricValue type="present">{presentCount}</MetricValue>
                        <MetricLabel>Present</MetricLabel>
                      </MetricItem>
                      <MetricItem>
                        <MetricValue type="absent">{absentCount}</MetricValue>
                        <MetricLabel>Absent</MetricLabel>
                      </MetricItem>
                    </AttendanceMetrics>
                    
                    <AttendanceRatePill>
                      {attendanceRate}% Attendance Rate
                    </AttendanceRatePill>
                  </SessionCard>
                );
              })}
            </SessionsGrid>
          )}
        </SessionsSection>
      )}

      {/* Scheduled (pending/created/failed) sessions for the selected entity. */}
      {selectedEntity && scheduledSessions.length > 0 && (
        <SessionsSection>
          <SessionsSectionHeader>
            <SessionsSectionTitle>Scheduled Sessions</SessionsSectionTitle>
          </SessionsSectionHeader>
          <ScheduledList>
            {scheduledSessions.map((s) => {
              const scheduledLocal = s.scheduled_at
                ? (() => {
                    const d = new Date(s.scheduled_at);
                    const datePart = d.toLocaleDateString('en-US', {
                      timeZone: 'America/Chicago',
                      month: 'short', day: 'numeric', year: 'numeric',
                    });
                    const timePart = d.toLocaleTimeString('en-US', {
                      timeZone: 'America/Chicago',
                      hour: 'numeric', minute: '2-digit',
                    });
                    return `${datePart} at ${timePart}`;
                  })()
                : '—';
              const sessionDateLabel = s.session_date
                ? new Date(`${String(s.session_date).slice(0, 10)}T00:00:00`).toLocaleDateString('en-US', {
                    month: 'short', day: 'numeric', year: 'numeric',
                  })
                : '—';
              // Format HH:MM:SS as "h:MM AM/PM" so the card matches the rest of
              // the attendance UI which presents times in 12-hour format.
              const formatTimeOfDay = (t) => {
                if (!t) return '—';
                const [hStr, mStr] = String(t).split(':');
                const h = parseInt(hStr, 10);
                const m = parseInt(mStr || '0', 10);
                if (Number.isNaN(h) || Number.isNaN(m)) return '—';
                const meridiem = h >= 12 ? 'PM' : 'AM';
                const h12 = h % 12 === 0 ? 12 : h % 12;
                return `${h12}:${String(m).padStart(2, '0')} ${meridiem}`;
              };
              const startLabel = formatTimeOfDay(s.start_time);
              const endLabel = formatTimeOfDay(s.end_time);
              return (
                <ScheduledCard key={s.id}>
                  <ScheduledMeta>
                    <ScheduledTitle>
                      {s.title || 'Untitled session'}
                      <ScheduledStatusPill $status={s.status}>{s.status}</ScheduledStatusPill>
                    </ScheduledTitle>
                    <ScheduledDetailRow>
                      Will be created on <strong>{scheduledLocal}</strong> · Session date: <strong>{sessionDateLabel}</strong> · Check-In Window: {startLabel} – {endLabel}
                    </ScheduledDetailRow>
                    {s.error_message && (
                      <ScheduledError>Error: {s.error_message}</ScheduledError>
                    )}
                  </ScheduledMeta>
                  {s.status === 'pending' && (
                    <ScheduledCancelButton
                      type="button"
                      onClick={() => handleCancelScheduledSession(s.id)}
                      disabled={cancellingScheduleId === s.id}
                    >
                      {cancellingScheduleId === s.id ? 'Cancelling…' : 'Cancel'}
                    </ScheduledCancelButton>
                  )}
                  {s.status === 'failed' && (
                    <ScheduledRetryButton
                      type="button"
                      onClick={() => handleRetryScheduledSession(s.id)}
                      disabled={retryingScheduleId === s.id}
                    >
                      {retryingScheduleId === s.id ? 'Retrying…' : 'Retry'}
                    </ScheduledRetryButton>
                  )}
                </ScheduledCard>
              );
            })}
          </ScheduledList>
        </SessionsSection>
      )}

      {showCreateModal && (
        <CreateAttendanceModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          course={selectedCourse}
          elective={selectedElective}
          onSessionCreated={handleSessionCreated}
        />
      )}
      {showTakeAttendanceModal && selectedSession && (
        <TakeAttendanceModal
          key={selectedSession.id}
          isOpen={showTakeAttendanceModal}
          onClose={() => {
            setShowTakeAttendanceModal(false);
            setLiveSessionStats(null); // Clear live stats when modal closes
          }}
          sessionId={selectedSession.id}
          onAttendanceUpdated={handleAttendanceUpdated}
          onAttendanceChanged={handleAttendanceChanged}
        />
      )}
      {showCourseSelection && (
        <CourseSelectionModal
          isOpen={showCourseSelection}
          onClose={() => setShowCourseSelection(false)}
          courses={courses}
          electives={electives}
          onCourseSelected={handleCourseSelected}
          onElectiveSelected={handleElectiveSelected}
        />
      )}
      {showSessionSelection && (
        <SessionSelectionModal
          isOpen={showSessionSelection}
          onClose={() => setShowSessionSelection(false)}
          sessions={sessions}
          onSessionSelected={handleSessionSelected}
        />
      )}
      {showQRCodeModal && selectedQRCodeSession && (
        <QRCodeModal
          isOpen={showQRCodeModal}
          onClose={() => {
            setShowQRCodeModal(false);
            setSelectedQRCodeSession(null);
          }}
          session={selectedQRCodeSession}
        />
      )}
      {showEditModal && selectedEditSession && (
        <EditAttendanceModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedEditSession(null);
          }}
          session={selectedEditSession}
          onSessionUpdated={handleSessionUpdated}
        />
      )}
      <Notification
        isOpen={notification.isOpen}
        onClose={() => setNotification({ ...notification, isOpen: false })}
        type={notification.type}
        title={notification.title}
        message={notification.message}
      />
    </Container>
  );
}

export default AttendanceManagement;
